import React from "react";
import { MikassaLogo } from "./Logo";
import { Linkedin, Facebook, Twitter } from "lucide-react";

const PARTNERS = [
  { name: "Orange Money", tag: "Mobile Money" },
  { name: "MTN Mobile Money", tag: "Mobile Money" },
  { name: "Twilio", tag: "SMS & Communication" },
  { name: "Supabase", tag: "Cloud Infrastructure" },
  { name: "Next.js", tag: "Web Framework" },
  { name: "React", tag: "UI Framework" },
];

const Footer = () => {
  const partnersDouble = [...PARTNERS, ...PARTNERS];

  return (
    <footer data-testid="site-footer" className="relative bg-[#0C2340] text-white overflow-hidden">
      {/* Building skyline decoration - above bg, below content */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" aria-hidden>
        <svg
          viewBox="0 0 1440 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          preserveAspectRatio="xMidYMax slice"
        >
          <path
            d="M0 200V180H40V140H60V100H80V140H100V120H120V80H140V60H160V80H180V60H200V40H220V60H240V100H260V80H280V120H300V100H320V140H340V120H360V80H380V100H400V60H420V80H440V120H460V100H480V140H500V120H520V160H540V140H560V100H580V120H600V80H620V60H640V100H660V80H680V120H700V100H720V140H740V120H760V80H780V100H800V60H820V80H840V40H860V60H880V100H900V80H920V120H940V100H960V140H980V120H1000V160H1020V140H1040V100H1060V120H1080V80H1100V100H1120V60H1140V80H1160V120H1180V100H1200V140H1220V120H1240V80H1260V100H1280V60H1300V80H1320V120H1340V100H1360V140H1380V120H1400V160H1420V140H1440V200H0Z"
            fill="#081828"
          />
          <path
            d="M0 200V190H30V160H50V130H70V160H90V140H110V110H130V90H150V110H170V90H190V70H210V90H230V120H250V100H270V140H290V120H310V160H330V140H350V100H370V120H390V80H410V100H430V140H450V120H470V160H490V140H510V180H530V160H550V130H570V150H590V110H610V90H630V130H650V110H670V150H690V130H710V170H730V150H750V110H770V130H790V90H810V110H830V70H850V90H870V130H890V110H910V150H930V130H950V170H970V150H990V180H1010V160H1030V130H1050V150H1070V110H1090V130H1110V90H1130V110H1150V150H1170V130H1190V170H1210V150H1230V110H1250V130H1270V90H1290V110H1310V150H1330V130H1350V170H1370V150H1390V180H1410V160H1440V200H0Z"
            fill="#091D2D"
          />
        </svg>
      </div>

      {/* Partners marquee strip */}
      <div className="relative z-10 border-t border-b border-white/10 overflow-hidden py-8">
        <div className="flex items-center gap-2 px-6 md:px-16 mb-6">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.65rem] uppercase tracking-[0.32em] text-white/50">
            Technologies & Intégrations
          </span>
        </div>
        <div className="relative overflow-hidden">
          <div className="mikassa-marquee-track flex gap-16 whitespace-nowrap w-max">
            {partnersDouble.map((p, i) => (
              <div key={`${p.name}-${i}`} className="flex flex-col justify-center min-w-[220px] px-6 border-l border-white/10">
                <span className="font-heading font-bold text-white text-lg tracking-tight">
                  {p.name}
                </span>
                <span className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-white/40 mt-1">
                  {p.tag}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <MikassaLogo inverse />
            <p className="mt-8 font-heading font-black text-white text-3xl md:text-5xl tracking-tighter leading-none max-w-md">
              La gestion locative,<br />
              <span className="text-[#E8611A]">enfin simplifiée.</span>
            </p>
            <p className="mt-8 font-body text-white/60 text-sm max-w-md leading-relaxed">
              Mikassa Agency — Logiciel de gestion locative conçu pour le marché ivoirien.
              Centralisez vos biens, locataires et finances en une seule plateforme.
            </p>
          </div>

          <div className="md:col-span-2">
            <div className="font-body text-[0.65rem] uppercase tracking-[0.28em] text-white/50 mb-4">
              Naviguer
            </div>
            <ul className="space-y-3 font-body text-sm text-white/80">
              <li><a href="#avantages" className="hover:text-[#E8611A]">Avantages</a></li>
              <li><a href="#fonctionnalites" className="hover:text-[#E8611A]">Fonctionnalités</a></li>
              <li><a href="#tarifs" className="hover:text-[#E8611A]">Tarifs</a></li>
              <li><a href="#temoignages" className="hover:text-[#E8611A]">Témoignages</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="font-body text-[0.65rem] uppercase tracking-[0.28em] text-white/50 mb-4">
              Fonctionnalités
            </div>
            <ul className="space-y-3 font-body text-sm text-white/80">
              <li>Gestion des Biens</li>
              <li>Gestion des Locataires</li>
              <li>Suivi Financier</li>
              <li>App Mobile</li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="font-body text-[0.65rem] uppercase tracking-[0.28em] text-white/50 mb-4">
              Contact
            </div>
            <ul className="space-y-3 font-body text-sm text-white/80">
              <li className="text-white/30">RCI - Abidjan, Cocody 2 Plateaux</li>
              <li><a href="mailto:contact@mikassa.app" className="hover:text-[#E8611A]">contact@mikassa.app</a></li>
              <li><a href="tel:+2250595476593" className="hover:text-[#E8611A]">+225 05 95 476 593</a></li>
            </ul>
            <div className="flex gap-4 mt-6">
              {[
                { Icon: Linkedin, href: "https://www.linkedin.com/company/mikassa-agency", label: "LinkedIn" },
                { Icon: Facebook, href: "https://web.facebook.com/profile.php?id=61577178491667", label: "Facebook" },
                { Icon: Twitter, href: null, label: "Twitter", disabled: true },
              ].map(({ Icon, href, label, disabled }, i) => (
                disabled ? (
                  <span
                    key={i}
                    className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/20 cursor-not-allowed"
                    aria-label={label}
                    data-testid={`social-${i}`}
                  >
                    <Icon size={16} strokeWidth={1.4} />
                  </span>
                ) : (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#E8611A] hover:border-[#E8611A] transition-colors"
                    aria-label={label}
                    data-testid={`social-${i}`}
                  >
                    <Icon size={16} strokeWidth={1.4} />
                  </a>
                )
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-white/40 font-body text-xs">
          <div>© 2026 Mikassa Agency · Tous droits réservés</div>
          <div className="flex flex-wrap gap-6">
            <span>Mentions légales</span>
            <span>Confidentialité</span>
            <span>CGU</span>
            <span>Support</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
